import React from 'react';

interface DrawingControlsProps {
  strokeColor: string;
  setStrokeColor: (color: string) => void;
  pointRadius: number;
  setPointRadius: (radius: number) => void;
  strokeWidth: number;
  setStrokeWidth: (width: number) => void;
  fillOpacity: number;
  setFillOpacity: (opacity: number) => void;
  onClose?: () => void;
}

export const DrawingControls: React.FC<DrawingControlsProps> = ({
  strokeColor,
  setStrokeColor,
  pointRadius,
  setPointRadius,
  strokeWidth,
  setStrokeWidth,
  fillOpacity,
  setFillOpacity,
  onClose,
}) => {
  return (
    <div className="bg-white p-4 sm:p-5 rounded-lg shadow-lg">
      <div className="flex justify-between items-center mb-3 sm:mb-4">
        <h3 className="text-sm sm:text-base font-semibold">Drawing Controls</h3>
        <button 
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 p-1 touch-manipulation"
          aria-label="Close drawing controls"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="space-y-4 sm:space-y-5">
        <div>
          <div className="flex justify-between text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
            <label htmlFor="stroke-color">Stroke/Fill Color:</label>
          </div>
          <input
            id="stroke-color"
            type="color"
            value={strokeColor}
            onChange={(e) => setStrokeColor(e.target.value)}
            className="h-8 w-full rounded cursor-pointer border border-gray-200 touch-manipulation"
            title="Choose stroke and fill color"
          />
        </div>

        <div>
          <div className="flex justify-between text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
            <label htmlFor="point-radius">Point Radius:</label>
            <span>{pointRadius}px</span>
          </div>
          <input
            id="point-radius"
            type="range"
            min="1"
            max="10"
            value={pointRadius}
            onChange={(e) => setPointRadius(Number(e.target.value))}
            className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer touch-manipulation
                     [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-500
                     [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-blue-500 [&::-moz-range-thumb]:border-0"
            title="Adjust point radius"
          />
        </div>

        <div>
          <div className="flex justify-between text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
            <label htmlFor="stroke-width">Stroke Width:</label>
            <span>{strokeWidth}px</span>
          </div>
          <input
            id="stroke-width"
            type="range"
            min="1"
            max="5"
            value={strokeWidth}
            onChange={(e) => setStrokeWidth(Number(e.target.value))}
            className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer touch-manipulation
                     [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-500
                     [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-blue-500 [&::-moz-range-thumb]:border-0"
            title="Adjust stroke width"
          />
        </div>

        <div>
          <div className="flex justify-between text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
            <label htmlFor="fill-opacity">Fill Opacity:</label>
            <span>{Math.round(fillOpacity * 100)}%</span>
          </div>
          <input
            id="fill-opacity"
            type="range"
            min="0"
            max="100"
            value={fillOpacity * 100}
            onChange={(e) => setFillOpacity(Number(e.target.value) / 100)}
            className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer touch-manipulation
                     [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-500
                     [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-blue-500 [&::-moz-range-thumb]:border-0"
            title="Adjust fill opacity"
          />
        </div>
      </div>
    </div>
  );
}; 